import React, { useEffect, useState } from "react";
import { Button, Form, Input } from "antd";
import trayaLogo1 from "../../../src/assets/img/illustrations/starfleet_light.png";
import LoginWithGoogle from "./LoginWithGoogle";
import { useNavigate } from "react-router-dom";
import Toaster from "modules/navbar/Toaster";
import { useToast } from "modules/navbar/top/store/toasterStore";
import { useSetUserDetails, userLoginStore } from "./store/store";
import { validateUrl } from "modules/utilities/utility_2";

// antd 5.x default primary blue (matches all other buttons/links in the app)
const PRIMARY = '#1677ff';
const PRIMARY_DARK = '#0958d9';
const PRIMARY_TEXT = '#ffffff';

const ANIM = `
  @keyframes loginSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

function Login() {
  const { setToaster, title, message, variant } = useToast();
  const { fetchLoginData, isLoading, res, forgotPassword } = userLoginStore();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const [view, setView] = useState("login"); // "login" | "forgot"
  const { setUserDetails } = useSetUserDetails();

  useEffect(() => {
    if (window.location.pathname !== "/login") navigate("/login");
  }, [navigate]);

  useEffect(() => {
    if (res === 200) {
      const t = setTimeout(() => setView("login"), 3000);
      return () => clearTimeout(t);
    }
  }, [res]);

  const handleSubmit = () => {
    fetchLoginData(email, password, setToaster, setUserDetails, navigate, validateUrl);
  };

  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center vh-100"
      style={{ backgroundColor: '#f0f2f5' }}
    >
      <style>{ANIM}</style>

      <Toaster title={title} variant={variant} message={message} />

      <div
        className="card shadow-sm"
        style={{
          width: '100%',
          maxWidth: '26rem',
          borderRadius: 12,
          border: '1px solid #e8e8e8',
          overflow: 'hidden',
          animation: 'loginSlideUp .45s ease both',
        }}
      >
        {/* Accent bar using Falcon primary */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${PRIMARY_DARK}, ${PRIMARY})` }} />

        <div className="card-body p-4">
          {/* Logo */}
          <div className="text-center mb-3">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 80, height: 80, borderRadius: '50%', background: '#344050' }}
            >
              <img src={trayaLogo1} alt="Traya" style={{ width: 52, filter: 'brightness(0) invert(1)' }} />
            </div>
            <div>
              <span
                className="badge"
                style={{ background: 'rgba(22,119,255,.12)', color: PRIMARY, fontWeight: 600, fontSize: '.72rem', letterSpacing: '.5px', textTransform: 'uppercase', padding: '.3rem .65rem', borderRadius: 999 }}
              >
                Secure sign-in
              </span>
            </div>
          </div>

          {/* key= replays animation on view switch */}
          <div key={view} style={{ animation: 'loginSlideUp .25s ease both' }}>
            {view === 'login' ? (
              <>
                <Form layout="vertical" onFinish={handleSubmit} requiredMark={false}>
                  <Form.Item label="Username" name="email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
                    <Input
                      placeholder="you@traya.health"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                  <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Please enter your password' }]}>
                    <Input.Password
                      placeholder="Password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      size="large"
                    />
                  </Form.Item>
                  <div className="text-end mb-3">
                    <a
                      href="#"
                      style={{ color: PRIMARY_DARK, fontSize: '.85rem', fontWeight: 600, textDecoration: 'none' }}
                      onClick={e => { e.preventDefault(); setView('forgot'); }}
                    >
                      Forgot password?
                    </a>
                  </div>
                  <Button
                    htmlType="submit"
                    size="large"
                    loading={isLoading}
                    block
                    style={{ background: PRIMARY, borderColor: PRIMARY, color: PRIMARY_TEXT, fontWeight: 600 }}
                  >
                    Login
                  </Button>
                </Form>

                {/* — or — divider */}
                <div className="d-flex align-items-center gap-2 my-3">
                  <hr className="flex-grow-1 m-0" style={{ borderColor: '#dee2e6' }} />
                  <span className="text-muted" style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}>or</span>
                  <hr className="flex-grow-1 m-0" style={{ borderColor: '#dee2e6' }} />
                </div>

                <div className="d-flex justify-content-center">
                  <LoginWithGoogle />
                </div>
              </>
            ) : (
              <Form layout="vertical" requiredMark={false}>
                <div className="text-center mb-3">
                  <div className="fw-bold mb-1" style={{ fontSize: '1rem', color: '#344050' }}>Reset your password</div>
                  <div className="text-muted" style={{ fontSize: '.85rem' }}>We'll email you a secure reset link</div>
                </div>
                <Form.Item label="Email" name="forgotEmail">
                  <Input
                    type="email"
                    placeholder="you@traya.health"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    size="large"
                  />
                </Form.Item>
                <div className="d-flex gap-2">
                  <Button
                    block
                    size="large"
                    style={{ background: PRIMARY, borderColor: PRIMARY, color: PRIMARY_TEXT, fontWeight: 600 }}
                    onClick={() => forgotPassword(email, setToaster)}
                  >
                    Send reset link
                  </Button>
                  <Button size="large" onClick={() => setView('login')}>
                    Back
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>

      <div className="text-muted mt-3" style={{ fontSize: '.8rem' }}>
        &copy; Traya Health &middot; Authorized CRM access only
      </div>
    </div>
  );
}

export default Login;
